'use client';

import { Config, Stage } from '@3fourteen/core';
import { downloadFile } from 'helpers/downloadFile';
import { useCustomResearch } from 'hooks/useCustomResearch';
import parse, { attributesToProps, domToReact, Element } from 'html-react-parser';
import { useEffect, useMemo, useRef, useState } from 'react';

import { LoadingDots } from 'components/LoadingDots';
import Image from 'next/image';
import Link from 'next/link';
import { IS_PRODUCTION } from 'services/environment';

// Cache presigned URLs and in-flight requests to reduce concurrent fetches
const presignedUrlCache = new Map<string, string>();
const presignedUrlPending = new Map<string, Promise<string>>();

function useOnScreen(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      {
        root: options?.root || null,
        rootMargin: options?.rootMargin || '200px',
        threshold: options?.threshold ?? 0,
      }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options?.root, options?.rootMargin, options?.threshold]);

  return { ref, isIntersecting } as const;
}

function ScriptExecutor({
  src,
  content,
  ...props
}: {
  src?: string;
  content?: string;
  [key: string]: any;
}): null {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const propsRef = useRef(props);

  // Update props ref when props change
  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  useEffect(() => {
    // Create a script element
    const script = document.createElement('script');

    // Set attributes from props
    Object.keys(propsRef.current).forEach((key) => {
      if (key !== 'children' && key !== 'key') {
        script.setAttribute(key, propsRef.current[key]);
      }
    });

    if (src) {
      // External script
      script.src = src;
      script.async = true;
    } else if (content) {
      // Inline script
      script.textContent = content;
    }

    // Append to document body
    document.body.appendChild(script);
    scriptRef.current = script;

    // Cleanup: remove script when component unmounts
    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [src, content]);

  // Scripts don't render anything
  return null;
}

function AuthImage({
  path,
  width,
  height,
  alt = '',
  className,
  style,
  ...restProps
}: {
  path: string;
  width?: number | string;
  height?: number | string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const stage = IS_PRODUCTION ? Stage.prod : Stage.dev;
  const { ref, isIntersecting } = useOnScreen();

  useEffect(() => {
    // Only fetch when image is in viewport (or already has URL)
    if (url) return; // already resolved
    if (!isIntersecting) return; // wait until near viewport

    let cancelled = false;

    async function fetchPresignedUrl() {
      const cleanPath = path.split('?')[0];

      // Check cache first
      if (presignedUrlCache.has(cleanPath)) {
        const cachedUrl = presignedUrlCache.get(cleanPath)!;
        if (!cancelled) setUrl(cachedUrl);
        return;
      }

      // Check if there's already a pending request
      if (presignedUrlPending.has(cleanPath)) {
        try {
          const pending = presignedUrlPending.get(cleanPath)!;
          const u = await pending;
          if (!cancelled) setUrl(u);
        } catch (err) {
          if (!cancelled) setError(err as Error);
        }
        return;
      }

      // Create new request
      const req = (async () => {
        try {
          const apiUrl = `${Config[stage].apiUrl}/custom-research/${cleanPath}`;
          const response = await fetch(apiUrl, {
            method: 'GET',
            credentials: 'include',
            headers: {
              Accept: 'application/json, text/plain, */*',
              'Content-Type': 'text/plain',
            },
          });
          if (!response.ok) {
            const message = `Failed to fetch presigned URL (${response.status}) for ${cleanPath}`;
            throw new Error(message);
          }
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json();
            const presignedUrl = data.url as string;
            if (!presignedUrl) {
              throw new Error('No URL in response');
            }
            presignedUrlCache.set(cleanPath, presignedUrl);
            return presignedUrl;
          } else {
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            presignedUrlCache.set(cleanPath, blobUrl);
            return blobUrl;
          }
        } catch (err) {
          throw err;
        }
      })();

      presignedUrlPending.set(cleanPath, req);
      try {
        const u = await req;
        if (!cancelled) setUrl(u);
      } catch (err) {
        if (!cancelled) setError(err as Error);
      } finally {
        presignedUrlPending.delete(cleanPath);
      }
    }

    fetchPresignedUrl();
    return () => {
      cancelled = true;
    };
  }, [isIntersecting, path, stage, url]);

  const parsedWidth =
    typeof width === 'string' ? parseInt(width, 10) : (width as number | undefined);
  const parsedHeight =
    typeof height === 'string' ? parseInt(height, 10) : (height as number | undefined);

  // Show loading placeholder or error
  if (!url) {
    if (error) {
      return (
        <span className='text-red-500 text-sm' title={error.message}>
          [Image failed to load]
        </span>
      );
    }
    // Reserve space to avoid layout shift; acts as the IntersectionObserver target
    // Use aspect ratio 2167:1369 (≈ 1.582) for images
    const aspectRatio = 2167 / 1369; // ≈ 1.582

    // Calculate dimensions based on aspect ratio if one dimension is missing
    let placeholderWidth: number | string | undefined = parsedWidth;
    let placeholderHeight: number | undefined = parsedHeight;

    if (!placeholderWidth && !placeholderHeight) {
      // No dimensions: use 100% width and maintain aspect ratio
      placeholderWidth = '100%';
      placeholderHeight = undefined; // Will use aspect-ratio CSS property
    } else if (placeholderWidth && !placeholderHeight) {
      // Only width: calculate height from aspect ratio
      placeholderHeight =
        typeof placeholderWidth === 'number' ? placeholderWidth / aspectRatio : undefined;
    } else if (!placeholderWidth && placeholderHeight) {
      // Only height: calculate width from aspect ratio
      placeholderWidth =
        typeof placeholderHeight === 'number' ? placeholderHeight * aspectRatio : undefined;
    }

    return (
      <div
        ref={ref}
        style={{
          display: 'inline-block',
          width: placeholderWidth || '100%',
          height: placeholderHeight,
          aspectRatio: !placeholderWidth || !placeholderHeight ? `${aspectRatio}` : undefined,
          backgroundColor: '#f0f0f0',
        }}
      />
    );
  }

  // Next.js Image doesn't support blob URLs, so use regular img for blob URLs
  const isBlobUrl = url.startsWith('blob:');

  // Calculate dimensions to match placeholder exactly and prevent layout shift
  const aspectRatio = 2167 / 1369; // ≈ 1.582
  let imageWidth: number | string | undefined = parsedWidth;
  let imageHeight: number | undefined = parsedHeight;

  if (!imageWidth && !imageHeight) {
    // No dimensions: use 100% width and maintain aspect ratio
    imageWidth = '100%';
    imageHeight = undefined; // Will use aspect-ratio CSS property
  } else if (imageWidth && !imageHeight) {
    // Only width: calculate height from aspect ratio
    imageHeight = typeof imageWidth === 'number' ? imageWidth / aspectRatio : undefined;
  } else if (!imageWidth && imageHeight) {
    // Only height: calculate width from aspect ratio
    imageWidth = typeof imageHeight === 'number' ? imageHeight * aspectRatio : undefined;
  }

  // Use Next.js Image for presigned URLs (not blob URLs) when dimensions are available
  if (!isBlobUrl && parsedWidth && parsedHeight) {
    return (
      <Image
        src={url}
        alt={alt}
        width={parsedWidth}
        height={parsedHeight}
        className={className}
        style={style}
        {...restProps}
      />
    );
  }

  // Use regular img tag for blob URLs or when dimensions aren't available
  // Match placeholder dimensions exactly to prevent layout shift
  const imgStyle: React.CSSProperties = {
    ...(typeof style === 'object' ? style : {}),
    display: 'inline-block',
    width: imageWidth || '100%',
    height: imageHeight,
    aspectRatio: !imageWidth || !imageHeight ? `${aspectRatio}` : undefined,
    maxWidth: '100%',
    objectFit: 'contain',
  };
  // eslint-disable-next-line @next/next/no-img-element -- dynamic HTML content, next/image not applicable
  return <img src={url} alt={alt} className={className} style={imgStyle} {...restProps} />;
}

const CustomResearchHtml = ({ filename, html }: { filename?: string; html?: string }) => {
  const { data: fetchedHtml, isLoading, error } = useCustomResearch(filename);
  const stage = IS_PRODUCTION ? Stage.prod : Stage.dev;

  // Use provided HTML if available, otherwise use fetched HTML
  const data = html || fetchedHtml;

  // Intercept fetch requests to rewrite relative /custom-research/ URLs to the API
  // This allows scripts in the HTML to fetch JSON/images from the API with proper auth
  useEffect(() => {
    const originalFetch = window.fetch;
    const apiUrl = Config[stage].apiUrl;

    // Wrap fetch to intercept custom-research URLs
    window.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      let url: string;
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else {
        url = input.url;
      }

      // Intercept URLs that match /custom-research/ pattern
      // Handle both relative URLs and absolute URLs pointing to the app domain
      const isAbsoluteUrl = url.startsWith('http://') || url.startsWith('https://');
      const isAppDomainUrl =
        isAbsoluteUrl &&
        (url.includes('localhost:3001') ||
          url.includes('app.3fourteenresearch.com') ||
          url.startsWith(window.location.origin));

      // Intercept relative URLs or absolute URLs pointing to app domain
      // Don't intercept absolute URLs pointing to API or external domains
      const isAlreadyApiUrl = isAbsoluteUrl && url.includes(Config[stage].apiUrl);

      // Only intercept JSON files
      if (
        url.includes('/custom-research/') &&
        url.endsWith('.json') &&
        !isAlreadyApiUrl &&
        (!isAbsoluteUrl || isAppDomainUrl)
      ) {
        // Extract the path after /custom-research/
        const match = url.match(/\/custom-research\/(.+)$/);
        if (match) {
          const customResearchPath = match[1];
          // It's a JSON file (we already checked with url.endsWith('.json'))
          const isJsonFile = true;

          // Rewrite to API URL
          const apiRequestUrl = `${apiUrl}/custom-research/${customResearchPath}`;

          // Prepare fetch options with proper headers
          // Preserve credentials from original request or use 'include'
          const originalCredentials =
            (input instanceof Request ? input.credentials : init?.credentials) || 'include';

          const fetchOptions: RequestInit = {
            ...init,
            credentials: originalCredentials === 'include' ? 'include' : originalCredentials,
            headers: {
              ...init?.headers,
              Accept: isJsonFile ? 'application/json' : 'application/json, text/plain, */*',
            },
          };

          // Use original fetch with rewritten URL
          let response: Response;
          if (typeof input === 'string') {
            response = await originalFetch(apiRequestUrl, fetchOptions);
          } else if (input instanceof URL) {
            response = await originalFetch(apiRequestUrl, fetchOptions);
          } else {
            // Request object - create new Request with rewritten URL
            const requestInit: RequestInit = {
              method: input.method,
              headers: {
                ...Object.fromEntries(input.headers.entries()),
                ...fetchOptions.headers,
              },
              mode: input.mode,
              credentials: originalCredentials === 'include' ? 'include' : originalCredentials,
              cache: input.cache,
              redirect: input.redirect,
              referrer: input.referrer,
              integrity: input.integrity,
              keepalive: input.keepalive,
              signal: input.signal,
            };

            if (input.body !== null) {
              requestInit.body = input.body;
            }

            response = await originalFetch(new Request(apiRequestUrl, requestInit));
          }

          // For JSON files, ensure proper content-type header
          if (isJsonFile && response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && !contentType.includes('application/json')) {
              // Response might be HTML (404 page) - clone to check without consuming body
              const clonedResponse = response.clone();
              const text = await clonedResponse.text();

              // Try to parse as JSON - if it fails, it's probably HTML
              try {
                JSON.parse(text); // Validate it's actually JSON
                // It's valid JSON, return with proper content-type
                return new Response(text, {
                  status: response.status,
                  statusText: response.statusText,
                  headers: {
                    'Content-Type': 'application/json',
                    ...Object.fromEntries(response.headers.entries()),
                  },
                });
              } catch {
                // Not JSON, probably HTML - return original response
                return response;
              }
            }
          }

          return response;
        }
      }

      // Not a custom-research URL, use original fetch
      return originalFetch(input, init);
    };

    // Cleanup: restore original fetch on unmount
    return () => {
      window.fetch = originalFetch;
    };
  }, [stage]);

  const options = useMemo(() => {
    if (data) {
      return {
        replace: (domNode: any) => {
          const { attribs } = domNode;

          if (!(domNode instanceof Element)) return;

          // Handle script tags
          if (domNode.name === 'script') {
            const props = attributesToProps(attribs);
            const scriptContent = domNode.children
              ?.map((child) => {
                if (child.type === 'text' && 'data' in child) {
                  return (child as unknown as { data: string }).data;
                }
                return '';
              })
              .join('')
              .trim();
            const scriptSrc = attribs?.src;

            return (
              <ScriptExecutor
                key={`script-${scriptSrc || scriptContent?.substring(0, 50)}`}
                src={scriptSrc}
                content={scriptContent}
                {...props}
              />
            );
          }

          // Handle image tags with src attributes
          if (domNode.name === 'img' && attribs?.src?.includes('/custom-research/')) {
            // Get everything after '/custom-research/'
            let fullPath = attribs.src.split('/custom-research/')[1];
            // Remove leading slash if present and strip query params
            fullPath = fullPath.replace(/^\/+/, '').split('?')[0];
            const props = attributesToProps(attribs);
            // Extract known props and exclude src (we'll use our own blob URL)
            const { width, height, style, className, alt, src, ...restProps } = props as any;

            // Replace with component that fetches presigned URL like the dynamic route
            // Use a key to ensure React properly replaces the original img element
            // Don't spread src from props - we'll use our own blob URL
            return (
              <AuthImage
                key={`auth-image-${fullPath}`}
                path={fullPath}
                width={width}
                height={height}
                alt={alt || ''}
                className={className}
                style={style}
                {...restProps}
              />
            );
          }

          // Handle links with href attributes
          if (!attribs?.href) return;

          if (attribs.href.includes('/custom-research/')) {
            // Get everything after '/custom-research/'
            const fullPath = attribs.href.split('/custom-research/')[1];
            const fileWithoutParams = fullPath.split('?')[0];
            const fileExtension = fileWithoutParams.split('.').pop();
            const fileWithoutExtension = fileWithoutParams.substring(
              0,
              fileWithoutParams.lastIndexOf('.')
            );
            const props = attributesToProps(attribs);

            if (fileExtension === 'csv') {
              return (
                <a
                  {...props}
                  href='#'
                  onClick={async (e) => {
                    e.preventDefault();
                    const stage = IS_PRODUCTION ? Stage.prod : Stage.dev;
                    const url = `${Config[stage].apiUrl}/custom-research/${fileWithoutParams}`;

                    await downloadFile(url, fileWithoutParams, {
                      method: 'GET',
                      credentials: 'include',
                      headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'text/plain',
                      },
                    });
                  }}>
                  {domToReact(domNode.children)}
                </a>
              );
            }

            return (
              <Link
                as={`/custom-research/${fullPath}`}
                href={`/custom-research/${fileWithoutExtension}`}
                style={props.style}
                className={props.className}>
                {domToReact(domNode.children)}
              </Link>
            );
          }
        },
      };
    } else {
      return {};
    }
  }, [data]);

  // Only show loading if we're fetching by filename (not when HTML is provided directly)
  if (isLoading && !html) {
    return (
      <div className='flex h-full items-center justify-center'>
        <LoadingDots color='#999' size='regular' />
      </div>
    );
  }

  if (!data) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen p-8'>
        <p className='text-xl font-semibold mb-2 text-red-700'>Unable to load this HTML file</p>
        {error && <p className='text-base text-gray-600 max-w-2xl text-center'>{error.message}</p>}
      </div>
    );
  }

  return <div className='min-h-screen '>{parse(data, options)}</div>;
};

export default CustomResearchHtml;
